import { modLoaderOf } from './addons.js';
// Modrinth API v2 の取得元。Mod とプラグインの両方を扱う。
import { z } from 'zod';
import { PackDownloadUrlSchema } from './modpack.js';
import type { AddonFetchJson } from './providers.js';
import {
  isPluginPlatform,
  ModFileSchema,
  SideSupportSchema,
  type AddonProject,
  type AddonSource,
  type AddonVersion,
  type ModLoader,
  type ModSearchHit,
  type SideSupport,
} from './mods.js';

export const MODRINTH_API = 'https://api.modrinth.com/v2';

/** 導入先ごとに、Modrinth で「動く」とみなすローダー名 */
export function modrinthLoaders(loader: ModLoader): string[] {
  if (loader === 'paper') return ['paper', 'spigot', 'bukkit'];
  if (loader === 'purpur') return ['purpur', 'paper', 'spigot', 'bukkit'];
  return [modLoaderOf(loader) ?? loader];
}

const sideSchema = z.string().transform((value): SideSupport => SideSupportSchema.safeParse(value).data ?? 'unknown');

const projectSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  description: z.string().default(''),
  icon_url: z.string().nullish(),
  project_type: z.string().optional(),
  loaders: z.array(z.string()).optional(),
  client_side: sideSchema,
  server_side: sideSchema,
});

const versionSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  version_number: z.string(),
  version_type: z.enum(['release', 'beta', 'alpha']),
  date_published: z.string(),
  loaders: z.array(z.string()),
  game_versions: z.array(z.string()),
  dependencies: z
    .array(
      z.object({
        version_id: z.string().nullish(),
        project_id: z.string().nullish(),
        dependency_type: z.enum(['required', 'optional', 'incompatible', 'embedded']),
      }),
    )
    .default([]),
  files: z.array(
    z.object({
      url: z.string().url(),
      filename: z.string(),
      primary: z.boolean().default(false),
      size: z.number().int().nonnegative(),
      hashes: z.object({ sha1: z.string(), sha512: z.string() }),
    }),
  ),
});
type RawVersion = z.infer<typeof versionSchema>;

const searchSchema = z.object({
  hits: z.array(
    z.object({
      project_id: z.string(),
      slug: z.string(),
      title: z.string(),
      description: z.string().default(''),
      icon_url: z.string().nullish(),
      downloads: z.number(),
      categories: z.array(z.string()).default([]),
      versions: z.array(z.string()).default([]),
      project_type: z.string().optional(),
      client_side: sideSchema,
      server_side: sideSchema,
    }),
  ),
});

interface ModrinthVersion extends AddonVersion {
  loaders: string[];
  gameVersions: string[];
}

function normalize(raw: RawVersion): ModrinthVersion {
  const primary = raw.files.find((f) => f.primary) ?? raw.files[0];
  const file = primary
    ? ModFileSchema.safeParse({
        url: primary.url,
        filename: primary.filename,
        sha1: primary.hashes.sha1,
        sha512: primary.hashes.sha512,
        size: primary.size,
      })
    : undefined;
  return {
    id: raw.id,
    projectId: raw.project_id,
    number: raw.version_number,
    type: raw.version_type,
    date: raw.date_published,
    loaders: raw.loaders,
    gameVersions: raw.game_versions,
    deps: raw.dependencies.map((d) => ({
      projectId: d.project_id ?? undefined,
      versionId: d.version_id ?? undefined,
      type: d.dependency_type,
    })),
    file: file?.success ? file.data : undefined,
  };
}

/** Modrinth API の薄いクライアント。HTTP（User-Agent・レート制限）は注入する fetchJson が担当する。 */
export class ModrinthClient implements AddonSource {
  readonly name = 'modrinth' as const;
  private readonly projects = new Map<string, AddonProject>();
  private readonly versions = new Map<string, ModrinthVersion>();

  async versionFiles(hashes: string[]): Promise<Map<string, string>> {
    if (!hashes.length) return new Map();
    const requested = z.array(z.string().regex(/^[0-9a-f]{40}$/)).parse([...new Set(hashes)]);
    const response = await this.fetchJson(this.base + '/version_files', {
      method: 'POST',
      body: JSON.stringify({ hashes: requested, algorithm: 'sha1' }),
    });
    const versions = z.record(z.string(), z.object({ files: versionSchema.shape.files })).parse(response);
    const result = new Map<string, string>();
    for (const hash of requested) {
      const file = versions[hash]?.files.find((entry) => entry.hashes.sha1.toLowerCase() === hash);
      if (file && new URL(file.url).hostname === 'cdn.modrinth.com') {
        result.set(hash, PackDownloadUrlSchema.parse(file.url));
      }
    }
    return result;
  }
  constructor(
    private readonly fetchJson: AddonFetchJson,
    private readonly base = MODRINTH_API,
  ) {}

  async searchModpacks(query: string, offset = 0) {
    const params = new URLSearchParams({
      query,
      facets: JSON.stringify([['project_type:modpack']]),
      limit: '20',
      offset: String(offset),
    });
    return searchSchema.parse(await this.fetchJson(`${this.base}/search?${params}`)).hits.map((hit) => ({
      source: 'modrinth' as const,
      projectId: hit.project_id,
      slug: hit.slug,
      title: hit.title,
      description: hit.description,
      iconUrl: hit.icon_url ?? undefined,
      downloads: hit.downloads,
      categories: hit.categories,
      versions: hit.versions,
      clientSide: hit.client_side,
      serverSide: hit.server_side,
    }));
  }

  async modpackVersions(projectId: string) {
    const raw = z
      .array(versionSchema)
      .parse(await this.fetchJson(`${this.base}/project/${encodeURIComponent(projectId)}/version`));
    const rank = { release: 0, beta: 1, alpha: 2 };
    return raw
      .filter((version) => version.project_id === projectId)
      .map((version) => {
        const file =
          version.files.find((entry) => entry.primary && entry.filename.endsWith('.mrpack')) ??
          version.files.find((entry) => entry.filename.endsWith('.mrpack'));
        return {
          id: version.id,
          number: version.version_number,
          type: version.version_type,
          date: version.date_published,
          loaders: version.loaders,
          gameVersions: version.game_versions,
          file: file
            ? { url: PackDownloadUrlSchema.parse(file.url), hashes: file.hashes, fileSize: file.size }
            : undefined,
        };
      })
      .filter((version) => version.file)
      .sort((a, b) => rank[a.type] - rank[b.type] || b.date.localeCompare(a.date));
  }

  async search(query: string, loader: ModLoader, mcVersion: string, offset = 0): Promise<ModSearchHit[]> {
    const type = isPluginPlatform(loader) ? 'plugin' : 'mod';
    // 同じ配列の中は OR、配列どうしは AND
    const facets = JSON.stringify([
      [`project_type:${type}`],
      modrinthLoaders(loader).map((l) => `categories:${l}`),
      ...(loader === 'velocity' ? [] : [[`versions:${mcVersion}`]]),
    ]);
    const params = new URLSearchParams({ query, facets, limit: '20', offset: String(offset) });
    const result = searchSchema.parse(await this.fetchJson(`${this.base}/search?${params}`));
    return result.hits.map((hit) => ({
      source: 'modrinth',
      projectId: hit.project_id,
      slug: hit.slug,
      title: hit.title,
      description: hit.description,
      iconUrl: hit.icon_url ?? undefined,
      downloads: hit.downloads,
      categories: hit.categories,
      clientSide: hit.client_side,
      serverSide: hit.server_side,
    }));
  }

  async project(idOrSlug: string): Promise<AddonProject> {
    const cached = this.projects.get(idOrSlug);
    if (cached) return cached;
    const raw = projectSchema.parse(await this.fetchJson(`${this.base}/project/${encodeURIComponent(idOrSlug)}`));
    const project: AddonProject = {
      ...addonKindsFromLoaders(raw.project_type, raw.loaders),
      kind:
        raw.project_type === 'mod' || raw.project_type === 'plugin'
          ? raw.project_type
          : raw.project_type
            ? 'unsupported'
            : undefined,
      id: raw.id,
      slug: raw.slug,
      title: raw.title,
      description: raw.description,
      iconUrl: raw.icon_url ?? undefined,
      client: raw.client_side,
      server: raw.server_side,
    };
    for (const key of [raw.id, raw.slug, idOrSlug]) this.projects.set(key, project);
    return project;
  }

  async version(id: string): Promise<AddonVersion> {
    const cached = this.versions.get(id);
    if (cached) return cached;
    const version = normalize(
      versionSchema.parse(await this.fetchJson(`${this.base}/version/${encodeURIComponent(id)}`)),
    );
    this.versions.set(version.id, version);
    return version;
  }

  async versionByHash(sha1: string): Promise<AddonVersion> {
    return normalize(versionSchema.parse(await this.fetchJson(`${this.base}/version_file/${sha1}?algorithm=sha1`)));
  }

  compatible(version: AddonVersion, loader: ModLoader, mcVersion: string): boolean {
    const v = version as ModrinthVersion;
    const loaders = modrinthLoaders(loader);
    return (
      (v.loaders ?? []).some((l) => loaders.includes(l)) &&
      (loader === 'velocity' || (v.gameVersions ?? []).includes(mcVersion))
    );
  }

  /** 導入先と MC バージョンに合う版のうち、release > beta > alpha、新しい順で最初のもの */
  async latestCompatible(
    projectId: string,
    loader: ModLoader,
    mcVersion: string,
    exactLoader = false,
  ): Promise<AddonVersion | undefined> {
    const params = new URLSearchParams({
      loaders: JSON.stringify(exactLoader ? [loader] : modrinthLoaders(loader)),
      game_versions: JSON.stringify([mcVersion]),
    });
    if (loader === 'velocity') params.delete('game_versions');
    const list = z
      .array(versionSchema)
      .parse(await this.fetchJson(`${this.base}/project/${encodeURIComponent(projectId)}/version?${params}`))
      .map(normalize);
    for (const version of list) this.versions.set(version.id, version);
    const rank = { release: 0, beta: 1, alpha: 2 } as const;
    return list
      .filter((v) => this.compatible(v, loader, mcVersion) && (!exactLoader || v.loaders.includes(loader)))
      .sort((a, b) => rank[a.type] - rank[b.type] || b.date.localeCompare(a.date))[0];
  }
}

const PLUGIN_LOADERS = new Set([
  'bukkit',
  'spigot',
  'paper',
  'purpur',
  'folia',
  'sponge',
  'bungeecord',
  'waterfall',
  'velocity',
]);
const MOD_LOADERS = new Set(['fabric', 'quilt', 'forge', 'neoforge', 'liteloader', 'modloader', 'rift']);
/**
 * Modrinth の v2 API はプラグインにも project_type: "mod" を返す。対応ローダーで、Mod・プラグインのどちらとして入れられるかを決める
 * （LuckPerms は fabric と paper の両方に対応するので両方）
 */
function addonKindsFromLoaders(type: string | undefined, loaders: string[] | undefined): Pick<AddonProject, 'kinds'> {
  if ((type !== 'mod' && type !== 'plugin') || !loaders?.length) return {};
  const kinds: ('mod' | 'plugin')[] = [];
  if (loaders.some((loader) => MOD_LOADERS.has(loader))) kinds.push('mod');
  if (loaders.some((loader) => PLUGIN_LOADERS.has(loader))) kinds.push('plugin');
  return kinds.length ? { kinds } : {};
}
